<script setup lang="ts">
import DRBtn from "@/components/UI/DRBtn.vue";
import { Logger } from "@/logger";
import { Facade } from "@/drawing";
import { useCreateEventLog } from "@/stores/queries";
import { EventType, type ParametricAsset } from "@/types";
import { trackTab } from "@/analytics/drawing-analytics";
import { computed, ref } from "vue";

const props = defineProps<{
  asset: ParametricAsset<any>;
}>();
const { createEvent } = useCreateEventLog();

const inititializeValues = () => {
  const values: Record<string, number | boolean> = {};
  for (const input of props.asset.inputs) {
    if (input.type === "boolean") {
      values[input.key] = true;
    }
  }
  return values;
};

const values = ref<Record<string, number | boolean>>(inititializeValues());
const show = ref(false);

function addAsset() {
  Logger.info(`AssetLibraryItem : add asset ${props.asset.id}`);

  const assetData: Record<string, number | boolean> = {};
  for (const input of props.asset.inputs) {
    assetData[input.key] = values.value[input.key] ?? 0;
  }

  if (Facade.isInitialized) {
    Facade.parametricAssets.add(props.asset.id, assetData);
  }

  show.value = false;
  // Left untouched so its historical series stays continuous.
  createEvent(EventType.USE_PARAMETRIC_ASSET);
  // Emitted alongside so the insertion also shows up in the drawing-usage
  // breakdown, next to the other features, with the asset as detail.
  trackTab("assets", "asset_inserted", { assetId: props.asset.id });
}

function handleInput(value: number | undefined, key: string) {
  setProperty(key, value ?? 0);
}

function setProperty(key: string, value: number) {
  if (!isNaN(value)) values.value[key] = value;
  else values.value[key] = 0;
}

function handleBoolean(value: boolean, key: string) {
  values.value[key] = value;
}

const inputs = computed(() => {
  if (props.asset.inputsToShow) {
    const list = props.asset.inputsToShow(values.value);
    return list.map((key) => {
      const input = props.asset.inputs.find((input) => input.key === key);
      return input;
    });
  }
  return props.asset.inputs;
});

const isValid = computed(() => {
  return inputs.value.every((input: any) => {
    const value = values.value[input.key];
    return (
      (input.type === "dimension" && (value as number) >= 0) ||
      (input.type === "angle" &&
        (value as number) >= 0 &&
        (value as number) <= 360) ||
      (input.type === "quantity" &&
        (value as number) >= 0 &&
        (value as number) % 1 === 0) ||
      (input.type === "boolean" && value !== undefined && value !== null)
    );
  });
});

const dialogImage = computed(() => {
  return props.asset.imageToShow
    ? props.asset.imageToShow(values.value)
    : props.asset.image;
});
</script>
<template>
  <button
    type="button"
    @click="show = !show"
    class="asset-card hoverable-item rounded border-0 p-0 overflow-hidden"
  >
    <img :src="asset.image" class="asset-thumb bordered rounded" alt="" />
    <div class="asset-name">
      {{ $t(`canvas:parametricAssets.library.${asset.id}.name`) }}
    </div>
  </button>

  <el-dialog
    v-model="show"
    :title="$t(`canvas:parametricAssets.library.${asset.id}.name`)"
    append-to-body
    top="1vh"
    :close-on-click-modal="false"
  >
    <div class="d-flex flex-column gap-3">
      <div
        class="asset-preview col-auto d-flex justify-content-center bordered rounded overflow-hidden position-relative"
      >
        <img :src="dialogImage" height="300" />
        <div
          class="position-absolute bottom-0 end-0 d-flex flex-column mx-3 my-1"
          v-if="props.asset.submittedBy"
        >
          <div class="text-grey text-center" style="font-size: 12px">
            {{ $t("canvas:settings.templates.proposedby") }}
          </div>
          <img
            :src="`/images/org/${props.asset.submittedBy.toString()}.png`"
            width="100"
          />
        </div>
      </div>
      <form class="col">
        <div class="d-flex flex-column gap-1">
          <template
            v-for="input in inputs"
            :key="input?.key ?? 'undefined' + '-key'"
          >
            <div v-if="input" class="row d-flex align-items-center">
              <div class="col-4">
                {{
                  $t(
                    `canvas:parametricAssets.library.${asset.id}.inputs.${input.key}.label`,
                  )
                }}
              </div>
              <div class="col-8">
                <el-button-group v-if="input.type === 'boolean'" class="ml-4">
                  <el-button
                    v-for="option in [
                      {
                        value: true,
                        label:
                          $t(
                            `canvas:parametricAssets.library.${asset.id}.inputs.${input.key}.booleanLabels.true`,
                          ) ?? $t('yes'),
                      },
                      {
                        value: false,
                        label:
                          $t(
                            `canvas:parametricAssets.library.${asset.id}.inputs.${input.key}.booleanLabels.false`,
                          ) ?? $t('no'),
                      },
                    ]"
                    :key="option.value.toString()"
                    :type="
                      option.value === values[input.key] ? 'primary' : 'default'
                    "
                    @click="handleBoolean(option.value, input.key)"
                    >{{ option.label }}</el-button
                  >
                </el-button-group>
                <el-input-number
                  v-if="input.type === 'dimension'"
                  size="large"
                  :step="0.1"
                  @update:model-value="(value) => handleInput(value, input.key)"
                  :model-value="values[input.key] as number"
                  :placeholder="
                    $t(
                      'canvas:parametricAssets.fieldType.dimension.placeholder',
                    )
                  "
                  :id="input.key"
                />
                <el-input-number
                  v-if="input.type === 'angle'"
                  size="large"
                  :step="0.1"
                  @update:model-value="(value) => handleInput(value, input.key)"
                  :model-value="values[input.key] as number"
                  :placeholder="
                    $t('canvas:parametricAssets.fieldType.angle.placeholder')
                  "
                  :id="input.key"
                />
                <el-input-number
                  v-if="input.type === 'quantity'"
                  :step="1"
                  size="large"
                  @update:model-value="(value) => handleInput(value, input.key)"
                  :model-value="values[input.key] as number"
                  :placeholder="
                    $t('canvas:parametricAssets.fieldType.quantity.placeholder')
                  "
                  :id="input.key"
                />
              </div>
            </div>
          </template>
        </div>
      </form>
    </div>
    <template #footer>
      <div class="d-flex gap-2 justify-content-between">
        <DRBtn variant="outline" :label="$t('cancel')" @click="show = false">
        </DRBtn>
        <DRBtn
          :label="$t('canvas:settings.templates.confirm')"
          @click="addAsset"
          :disabled="!isValid"
        >
        </DRBtn>
      </div>
    </template>
  </el-dialog>
</template>
<style scoped lang="scss">
// Le schéma de l'objet est dessiné sur fond blanc : la zone reste blanche même
// dans la modale sombre de l'outil dessin.
.asset-preview {
  background-color: #fff;
}

// Carte de la grille Objets : toutes de même hauteur dans une rangée, schémas
// dans un cadre identique (la plupart font 740×609 ; les autres y sont
// contenus), nom sur 2 lignes au plus.
.asset-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.asset-thumb {
  display: block;
  width: 100%;
  aspect-ratio: 740 / 609;
  object-fit: contain;
  background-color: #fff;
}

.asset-name {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  min-height: calc(2 * 1.25em + 8px);
  padding: 4px 2px;
  overflow: hidden;
  font-size: 12px;
  line-height: 1.25;
  text-align: center;
}

.hoverable-item {
  background-color: transparent;
  color: var(--bs-grey);
  opacity: 0.7;
  transition: all 0.3s ease-in-out;
  &:hover {
    opacity: 1;
    color: var(--bs-white);
  }
}
</style>
